import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const COMPACT_CLASS =
  'inline-flex items-center justify-center p-1 rounded-lg transition hover:opacity-80';
const COMPACT_DARK_CLASS = 'text-slate-200';
const COMPACT_LIGHT_CLASS = 'text-black';
const FULL_BUTTON_CLASS =
  'group flex items-center gap-2 rounded-xl border border-white/60 bg-white/60 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-white';

/**
 * Renders a "View All" / "See More" style action that is either:
 * - Compact: icon-only (arrow) on mobile/native
 * - Full: button with label + arrow on desktop
 * Use with useCompactViewAll() for the compact prop.
 *
 * @param {object} props
 * @param {boolean} props.compact - From useCompactViewAll(); true = icon only
 * @param {string} [props.to] - If set, render as Link; otherwise as button
 * @param {() => void} [props.onClick] - Required when `to` is not set
 * @param {string} props.label - e.g. "View All" or "See More"
 * @param {string} props.ariaLabel - Accessible label
 * @param {boolean} [props.isDarkTheme] - Use dark-theme compact styling
 * @param {string} [props.className] - Extra class for wrapper (e.g. when Link)
 */
function CompactOrFullAction({
  compact,
  to,
  onClick,
  label,
  ariaLabel,
  isDarkTheme = false,
  className,
}) {
  const compactClassName = cn(
    COMPACT_CLASS,
    isDarkTheme ? COMPACT_DARK_CLASS : COMPACT_LIGHT_CLASS
  );

  if (compact) {
    const content = <ArrowRight className="h-5 w-5" />;
    if (to != null) {
      return (
        <Link
          to={to}
          className={cn(compactClassName, className)}
          aria-label={ariaLabel}
        >
          {content}
        </Link>
      );
    }
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(compactClassName, className)}
        aria-label={ariaLabel}
      >
        {content}
      </button>
    );
  }

  const fullContent = (
    <>
      {label}
      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
    </>
  );
  if (to != null) {
    return (
      <Button asChild variant="ghost" className={FULL_BUTTON_CLASS}>
        <Link to={to} aria-label={ariaLabel}>
          {fullContent}
        </Link>
      </Button>
    );
  }
  return (
    <Button
      variant="ghost"
      className={FULL_BUTTON_CLASS}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {fullContent}
    </Button>
  );
}

export default CompactOrFullAction;
